import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '@/lib/api';
import openHtmlInNewTab from '@/lib/openHtmlInNewTab';
// Phase 185.e — popup-blocker-proof signed view links
import openSignedDocInNewTab from '@/lib/openSignedDocInNewTab';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { CalendarCheck, IndianRupee, Award, CreditCard, Clock, CheckCircle, XCircle, AlertTriangle, Star, MapPin, Calendar as CalendarIcon, Users, Sparkles, HelpCircle, X, Download, Heart, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
// Phase 249-B — Year-End Fee Report self-service for parents (uses Phase 102
// `selectedChildId` so multi-child households can download per-child statements).
import YearEndReportCard from '@/components/finance/YearEndReportCard';

export default function ParentPortal() {
  // Phase 104 — Targeted Practice Kit (defined below the main component)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  // Phase 65.7 — training sessions, progress cards (lazy-loaded per tab)
  // Phase 86 — Events moved to standalone /events route (removed from Parent Portal tabs)
  const [activeTab, setActiveTab] = useState('overview');
  const [sessions, setSessions] = useState(null);
  const [progressCards, setProgressCards] = useState(null);
  // Phase 102 — Multi-child support
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState(null);
  // Phase 103 — Injury eligibility for the active child
  const [eligibility, setEligibility] = useState(null);
  // Phase 254-EmailDeepLink-Parent — one-shot capture of `?fee_id=` from the
  // landing URL. We use a ref (not state) so the value survives the data
  // refetch that happens when the parent's active child auto-switches
  // mid-flow. Cleared once the fee is either armed for payment or proven
  // unresolvable.
  const pendingFeeIdRef = useRef(null);
  const pendingFeeCapturedRef = useRef(false);
  const locatingFeeRef = useRef(false);
  // Phase 254-Bulk-Pay — household-wide consolidated cart + bulk Razorpay flow
  const [bulkCart, setBulkCart] = useState(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkPaying, setBulkPaying] = useState(false);
  const location = useLocation();

  const refresh = async (childId = null) => {
    try {
      const params = {};
      const cid = childId || selectedChildId;
      if (cid) params.student_id = cid;
      const [res, eligRes] = await Promise.all([
        api.get('/parent/dashboard', { params }),
        cid ? api.get(`/students/${cid}/eligibility`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      ]);
      setData(res.data);
      setEligibility(eligRes.data);
    } catch (err) {
      toast.error('Failed to load parent dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Phase 102 — Hydrate children list once on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/parent/children');
        const list = res.data || [];
        setChildren(list);
        const firstId = list[0]?.id || null;
        setSelectedChildId(firstId);
        await refresh(firstId);
      } catch (err) {
        // If children fetch fails (e.g. legacy parent with only student_id), fall through to default
        await refresh();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When child switches, refresh dashboard & invalidate lazy-loaded tabs
  const handleSwitchChild = (childId) => {
    if (childId === selectedChildId) return;
    setSelectedChildId(childId);
    setSessions(null);
    setProgressCards(null);
    setLoading(true);
    refresh(childId);
  };

  // Lazy-load secondary tabs only when first opened — keeps the dashboard fast
  useEffect(() => {
    const params = selectedChildId ? { student_id: selectedChildId } : {};
    if (activeTab === 'training' && sessions === null) {
      api.get('/parent/training-sessions', { params }).then(r => setSessions(r.data || [])).catch(() => setSessions([]));
    }
    if (activeTab === 'progress' && progressCards === null) {
      api.get('/parent/progress-cards', { params }).then(r => setProgressCards(r.data || [])).catch(() => setProgressCards([]));
    }
  }, [activeTab, sessions, progressCards, selectedChildId]);

  // Phase 254-EmailDeepLink-Parent — when the parent lands here from a fee
  // reminder carrying `?fee_id=<X>`, auto-launch Razorpay for that exact
  // fee. Cross-child aware: if the deep-linked fee belongs to a sibling
  // (not the currently-selected child), we hit `/parent/fees/{id}/locate`
  // to discover the owning child, auto-switch the active profile, and
  // re-resolve on the next data tick. A flat refetch cycle.
  useEffect(() => {
    // Phase A — capture the URL token exactly once per page-load.
    if (!pendingFeeCapturedRef.current) {
      const params = new URLSearchParams(location.search);
      const targetId = params.get('fee_id');
      if (targetId) {
        pendingFeeIdRef.current = targetId;
        // Strip the param immediately so refresh / navigation can't re-fire.
        window.history.replaceState({}, '', location.pathname);
      }
      pendingFeeCapturedRef.current = true;
    }
    // Phase B — resolve the pending fee against whichever child is loaded.
    if (loading || !data) return;
    const pending = pendingFeeIdRef.current;
    if (!pending) return;
    if (locatingFeeRef.current) return;
    const allFees = data?.fees?.all || [];
    const target = allFees.find((f) => f.id === pending);
    if (target) {
      // Found in current child's data — arm the payment handler.
      pendingFeeIdRef.current = null;
      if (target.status === 'paid') {
        toast.info('This fee is already paid.');
        return;
      }
      if (target.status === 'cancelled') return;
      setActiveTab('fees');
      toast.info(`Launching payment for ${target.fee_type || 'fee'}…`);
      Promise.resolve().then(() => handlePay(target));
      return;
    }
    // Not in current child's data — ask the backend which sibling it belongs to.
    locatingFeeRef.current = true;
    api.get(`/parent/fees/${pending}/locate`)
      .then((r) => {
        const siblingId = r.data?.student_id;
        const siblingName = r.data?.student_name || 'sibling';
        if (siblingId && siblingId !== selectedChildId) {
          toast.info(`Switching to ${siblingName} to pay this fee…`);
          handleSwitchChild(siblingId);
          // Don't null pendingFeeIdRef — the upcoming data refetch will
          // re-enter this effect and the find() will hit.
        } else {
          // Locate returned the same child somehow (race?) or the fee is
          // not in `fees.all` because the dashboard caps it at 50 rows.
          // Drop the deep-link silently — user still sees the dashboard.
          pendingFeeIdRef.current = null;
        }
      })
      .catch(() => {
        // 404 / 403 — fee doesn't belong to this parent (or is missing).
        // Drop silently.
        pendingFeeIdRef.current = null;
      })
      .finally(() => {
        locatingFeeRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data, location.search]);

  // Phase 254-Bulk-Pay — fetch the household consolidated cart whenever the
  // parent's dashboard data refreshes (so a successful single-fee payment
  // immediately re-hydrates the banner). Silent on error — the banner just
  // stays hidden.
  const fetchBulkCart = useCallback(async () => {
    try {
      const r = await api.get('/parent/fees/consolidated-cart');
      setBulkCart(r.data || null);
    } catch (err) {
      setBulkCart(null);
    }
  }, []);

  useEffect(() => {
    if (!loading && data) fetchBulkCart();
  }, [loading, data, fetchBulkCart]);

  const handleBulkPay = async () => {
    if (!bulkCart || !bulkCart.eligible || bulkPaying) return;
    setBulkPaying(true);
    // Collect every fee_id across all children
    const feeIds = (bulkCart.by_child || []).flatMap((c) => (c.fees || []).map((f) => f.id));
    try {
      const orderRes = await api.post('/parent/fees/bulk-create-order', { fee_ids: feeIds });
      const order = orderRes.data;
      if (order.mock_mode) {
        toast.info('Academy has not configured online payments yet. Please contact academy.');
        setBulkPaying(false);
        setBulkDialogOpen(false);
        return;
      }
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        await new Promise((r) => { script.onload = r; setTimeout(r, 3000); });
      }
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount_in_paise,
        currency: order.currency || 'INR',
        order_id: order.razorpay_order_id,
        name: order.academy_name || 'Academy',
        description: `Household Bulk Fee Settlement — ${order.fees_count} item${order.fees_count !== 1 ? 's' : ''}`,
        handler: async (response) => {
          try {
            const v = await api.post('/parent/fees/bulk-verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success(`Settled ${v.data?.fees_settled || order.fees_count} fees for your household!`);
            setBulkDialogOpen(false);
            refresh(selectedChildId);
          } catch (err) {
            toast.error('Verification failed. Please contact your academy.');
          }
          setBulkPaying(false);
        },
        modal: {
          ondismiss: () => {
            toast.info('Bulk payment cancelled');
            setBulkPaying(false);
          },
        },
        theme: { color: '#0f766e' },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start bulk payment');
      setBulkPaying(false);
    }
  };

  const handlePay = async (fee) => {
    setPaying(fee.id);
    try {
      const orderRes = await api.post(`/fees/${fee.id}/create-order`);
      const order = orderRes.data;
      if (order.status === 'paid') { toast.info('Already paid'); refresh(selectedChildId); setPaying(null); return; }
      if (order.mock_mode) {
        toast.info('Academy has not configured online payments yet. Please contact academy.');
        setPaying(null); return;
      }
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        await new Promise(r => { script.onload = r; setTimeout(r, 3000); });
      }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency || 'INR',
        order_id: order.order_id,
        name: order.academy_name || 'Academy',
        description: `Fee — ${data.student.name}`,
        handler: async (response) => {
          try {
            await api.post(`/fees/${fee.id}/verify-payment`, response);
            toast.success('Payment successful!');
            refresh(selectedChildId);
          } catch { toast.error('Verification failed'); }
          setPaying(null);
        },
        modal: { ondismiss: () => { toast.info('Payment cancelled'); setPaying(null); } },
        theme: { color: '#0f766e' },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start payment');
      setPaying(null);
    }
  };

  if (loading) return (
    <div className="space-y-4 p-4" data-testid="parent-loading">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );

  if (!data) return <div className="p-8 text-center text-muted-foreground">Unable to load child data</div>;

  const { student, academy, attendance, fees, latest_progress } = data;
  const skills = latest_progress?.skills_rating || {};
  const skillData = Object.entries(skills).map(([k, v]) => ({ skill: k, rating: parseFloat(v) || 0 }));

  return (
    <div className="space-y-6" data-testid="parent-portal">
      {/* Phase 102 — Multi-child switcher (only renders when >1 child linked) */}
      {children.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="parent-child-switcher">
          <span className="text-xs font-medium text-muted-foreground mr-1">Viewing:</span>
          {children.map((c) => {
            const active = c.id === selectedChildId;
            const initials = (c.name || '??').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => handleSwitchChild(c.id)}
                data-testid={`parent-child-switch-${c.id}`}
                className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border transition-all ${
                  active
                    ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                    : 'bg-background text-foreground border-border hover:border-teal-400'
                }`}
              >
                <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                  active ? 'bg-white/20 text-white' : 'bg-teal-100 text-teal-700'
                }`}>{initials}</span>
                <span className="text-xs font-medium">{c.name}</span>
                {c.sport && <span className={`text-[10px] ${active ? 'opacity-80' : 'text-muted-foreground'}`}>· {c.sport}</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Phase 103 — Injury / field-eligibility banner for the active child */}
      {eligibility && eligibility.eligibility !== 'eligible' && (
        <div
          className={`rounded-lg border p-4 flex items-start gap-3 ${
            eligibility.eligibility === 'not_eligible'
              ? 'border-rose-300 bg-rose-50 dark:bg-rose-950/20'
              : 'border-amber-300 bg-amber-50 dark:bg-amber-950/20'
          }`}
          data-testid="parent-injury-banner"
        >
          <Heart className={`h-5 w-5 mt-0.5 shrink-0 ${eligibility.eligibility === 'not_eligible' ? 'text-rose-600' : 'text-amber-600'}`} />
          <div className="min-w-0 flex-1">
            <p className={`font-medium text-sm ${eligibility.eligibility === 'not_eligible' ? 'text-rose-900' : 'text-amber-900'}`}>
              {eligibility.eligibility === 'not_eligible' ? 'Field eligibility: NOT ELIGIBLE' : 'Field eligibility: LIMITED DUTY'}
            </p>
            <ul className="mt-1 space-y-0.5">
              {(eligibility.restrictions || []).map((r, i) => (
                <li key={i} className="text-xs text-muted-foreground">• {r}</li>
              ))}
            </ul>
            {(eligibility.active_injuries || []).some(i => i.expected_recovery_date) && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Expected recovery: {(eligibility.active_injuries[0] || {}).expected_recovery_date}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-sm opacity-90">Parent Portal</p>
            <h1 className="text-2xl font-display font-bold mt-1" data-testid="parent-child-name">{student.name}</h1>
            <p className="text-sm opacity-90 mt-1">{student.custom_id} · {student.batch} · {student.course || student.sport || ''}</p>
            <p className="text-xs opacity-75 mt-2">{academy.name} {academy.city ? `· ${academy.city}` : ''}</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <div className="bg-white/20 backdrop-blur rounded-xl px-4 py-2.5">
              <p className="text-xs opacity-90">Attendance (30d)</p>
              <p className="text-2xl font-bold tabular-nums">{attendance.rate}%</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-xl px-4 py-2.5">
              <p className="text-xs opacity-90">Pending Fees</p>
              <p className="text-2xl font-bold tabular-nums">₹{fees.pending_amount.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="overview" data-testid="parent-tab-overview"><Award className="h-4 w-4 mr-1" /> Overview</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="parent-tab-attendance"><CalendarCheck className="h-4 w-4 mr-1" /> Attendance</TabsTrigger>
          <TabsTrigger value="fees" data-testid="parent-tab-fees"><IndianRupee className="h-4 w-4 mr-1" /> Fees</TabsTrigger>
          <TabsTrigger value="progress" data-testid="parent-tab-progress"><Sparkles className="h-4 w-4 mr-1" /> Progress Cards</TabsTrigger>
          <TabsTrigger value="training" data-testid="parent-tab-training"><Users className="h-4 w-4 mr-1" /> Training</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4">
          {/* Phase 254-Bulk-Pay — Household Mega-Cart Banner (Overview tab) */}
          {bulkCart && bulkCart.eligible && bulkCart.fees_count > 1 && (
            <div
              data-testid="parent-bulk-banner"
              className="rounded-xl bg-gradient-to-r from-teal-700 via-emerald-700 to-emerald-800 text-white p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-md ring-1 ring-emerald-900/30"
            >
              <div className="flex items-start gap-3">
                <div className="p-2.5 rounded-lg bg-white/15 backdrop-blur-sm flex-shrink-0">
                  <Layers className="h-5 w-5" />
                </div>
                <div className="space-y-0.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-100">
                    Pay All Pending Fees · {bulkCart.academy_name}
                  </p>
                  <p className="text-base sm:text-lg font-display font-bold">
                    ₹{(bulkCart.total_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} due across {bulkCart.fees_count} bill{bulkCart.fees_count !== 1 ? 's' : ''}
                  </p>
                  <p className="text-[11px] text-emerald-100/90">
                    Settle every child&apos;s outstanding fee in one Razorpay transaction.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setBulkDialogOpen(true)}
                className="bg-white text-emerald-800 hover:bg-emerald-50 font-semibold flex-shrink-0"
                data-testid="parent-bulk-banner-pay-btn"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Pay All ₹{Math.round(bulkCart.total_amount || 0).toLocaleString('en-IN')}
              </Button>
            </div>
          )}
          {/* Multi-academy notice (informational — no CTA) */}
          {bulkCart && !bulkCart.eligible && bulkCart.reason === 'multi_academy' && bulkCart.fees_count > 0 && (
            <div
              data-testid="parent-bulk-multi-academy-notice"
              className="rounded-xl bg-slate-50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-700 p-3 flex items-start gap-3"
            >
              <AlertTriangle className="h-4 w-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-slate-600 dark:text-slate-300">
                You have pending fees across {bulkCart.academy_count || 'multiple'} academies. Each academy needs its own payment — use the
                <strong> Pay </strong>button next to each fee in the Fees tab.
              </div>
            </div>
          )}
          {/* Quick Stats */}
          <div className="grid sm:grid-cols-3 gap-3">
            <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <CalendarCheck className="h-4 w-4 text-emerald-600" />
                  <Badge variant="outline" className="text-[10px]">Last 30d</Badge>
                </div>
                <p className="text-2xl font-display font-bold tabular-nums text-emerald-700">{attendance.rate}%</p>
                <p className="text-xs text-emerald-600">{attendance.present} of {attendance.total} sessions</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <IndianRupee className="h-4 w-4 text-amber-600" />
                  <Badge variant="outline" className="text-[10px]">{fees.pending_count} bills</Badge>
                </div>
                <p className="text-2xl font-display font-bold tabular-nums text-amber-700">₹{(fees.pending_amount/1000).toFixed(1)}K</p>
                <p className="text-xs text-amber-600">Outstanding</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <Award className="h-4 w-4 text-blue-600" />
                  <Badge variant="outline" className="text-[10px]">Latest</Badge>
                </div>
                <p className="text-2xl font-display font-bold tabular-nums text-blue-700">{Object.keys(skills).length}</p>
                <p className="text-xs text-blue-600">Skills assessed</p>
              </CardContent>
            </Card>
          </div>

          {/* Skill Radar */}
          {skillData.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Star className="h-4 w-4 text-amber-500" />
                  Performance Snapshot
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={skillData}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="skill" tick={{ fontSize: 12 }} />
                    <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                    <Radar name="Rating" dataKey="rating" stroke="#0f766e" fill="#0f766e" fillOpacity={0.4} />
                  </RadarChart>
                </ResponsiveContainer>
                {latest_progress?.comments && (
                  <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Coach feedback:</p>
                    <p className="text-sm">{latest_progress.comments}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="attendance" className="mt-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Recent Attendance</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {attendance.recent.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No attendance records yet.</p>
              ) : attendance.recent.map((r, idx) => (
                <div key={idx} className="flex items-center justify-between border-b border-border/50 py-2 last:border-0" data-testid={`parent-att-row-${idx}`}>
                  <span className="text-sm">{r.date}</span>
                  <Badge
                    variant="outline"
                    className={
                      r.status === 'present' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' :
                      r.status === 'absent' ? 'bg-red-50 text-red-700 border-red-300' :
                      r.status === 'late' ? 'bg-amber-50 text-amber-700 border-amber-300' :
                      'bg-slate-50 text-slate-700 border-slate-300'
                    }
                  >
                    {r.status === 'present' ? <CheckCircle className="h-3 w-3 mr-1" /> :
                     r.status === 'absent' ? <XCircle className="h-3 w-3 mr-1" /> :
                     <Clock className="h-3 w-3 mr-1" />}
                    {r.status}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fees" className="mt-4">
          {/* Phase 249-B — Tax-season Year-End Fee Report (per-child for multi-child households) */}
          {selectedChildId && (
            <div className="mb-4">
              <YearEndReportCard mode="self" studentId={selectedChildId} />
            </div>
          )}
          {/* Phase 254-Bulk-Pay — Inline Pay-All call-to-action above the per-child fee list */}
          {bulkCart && bulkCart.eligible && bulkCart.fees_count > 1 && (
            <div
              data-testid="parent-fees-tab-bulk-cta"
              className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 px-4 py-3"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Layers className="h-4 w-4 text-emerald-700 dark:text-emerald-300 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 truncate">
                    Settle everything in one go
                  </p>
                  <p className="text-[11px] text-emerald-700 dark:text-emerald-300">
                    ₹{(bulkCart.total_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} across {bulkCart.fees_count} bills · all children
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => setBulkDialogOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0"
                data-testid="parent-fees-tab-bulk-pay-btn"
              >
                <CreditCard className="h-3.5 w-3.5 mr-1.5" />
                Pay All
              </Button>
            </div>
          )}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">All Fees</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {fees.all.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No fees billed yet.</p>
              ) : fees.all.map((f) => (
                <div key={f.id} className="flex items-center justify-between border-b border-border/50 py-3 last:border-0 gap-3 flex-wrap" data-testid={`parent-fee-row-${f.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{f.fee_type || 'Fee'} · {f.description || `Due ${f.due_date}`}</p>
                    <p className="text-xs text-muted-foreground">Due: {f.due_date}</p>
                  </div>
                  <p className="font-semibold tabular-nums">₹{f.amount.toLocaleString()}</p>
                  {f.status === 'paid' ? (
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300"><CheckCircle className="h-3 w-3 mr-1" /> Paid</Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => openSignedDocInNewTab(
                          // Phase 185.e — signed-token URL is popup-blocker-proof.
                          // Replaces the legacy fetch+inject pattern.
                          `/receipts/fee/${f.id}`,
                          { label: 'Receipt', errorMessage: 'Receipt not available yet' },
                        )}
                        data-testid={`parent-fee-receipt-${f.id}`}
                        title="Download Receipt"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => handlePay(f)}
                      disabled={paying === f.id}
                      data-testid={`parent-fee-pay-${f.id}`}
                    >
                      {paying === f.id ? <Clock className="h-3.5 w-3.5 animate-spin" /> : <><CreditCard className="h-3.5 w-3.5 mr-1" /> Pay</>}
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
          {fees.pending_amount > 0 && (
            <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-900">Pending payments</p>
                <p className="text-xs text-amber-700">₹{fees.pending_amount.toLocaleString()} across {fees.pending_count} fee{fees.pending_count !== 1 ? 's' : ''}.</p>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Phase 65.7 — Progress Cards */}
        <TabsContent value="progress" className="mt-4 space-y-3">
          {progressCards === null ? (
            <Skeleton className="h-32 w-full rounded-lg" />
          ) : progressCards.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No progress cards yet — your coach will publish one after assessments.</CardContent></Card>
          ) : progressCards.map((c, idx) => (
            <Card key={c.id || idx} data-testid={`parent-progress-card-${idx}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between gap-2">
                  <span>{c.title || `Progress Report — ${c.period || (c.created_at || '').slice(0, 10)}`}</span>
                  {c.coach_name && <Badge variant="outline" className="text-[10px]">by {c.coach_name}</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {c.skills && Object.keys(c.skills).length > 0 && (
                  <ResponsiveContainer width="100%" height={220}>
                    <RadarChart data={Object.entries(c.skills).map(([skill, rating]) => ({ skill, rating }))}>
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="skill" tick={{ fontSize: 11 }} />
                      <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 9 }} />
                      <Radar name="Rating" dataKey="rating" stroke="#0f766e" fill="#0f766e" fillOpacity={0.4} />
                    </RadarChart>
                  </ResponsiveContainer>
                )}
                {c.comments && (
                  <div className="p-3 bg-muted/40 rounded-lg">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Coach feedback</p>
                    <p className="text-sm">{c.comments}</p>
                  </div>
                )}
                {/* Phase 104 — Targeted Practice Kit */}
                <TargetedPracticeKit cardId={c.id} />
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Phase 65.7 — Training Sessions */}
        <TabsContent value="training" className="mt-4 space-y-3">
          {sessions === null ? (
            <Skeleton className="h-32 w-full rounded-lg" />
          ) : sessions.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No upcoming training sessions for {data?.student?.name || 'your child'}.</CardContent></Card>
          ) : sessions.map((s) => (
            <Card key={s.id} data-testid={`parent-training-${s.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-start justify-between gap-2">
                  <span className="break-words">{s.title}</span>
                  {s.coach_name && <Badge variant="outline" className="text-[10px] shrink-0">{s.coach_name}</Badge>}
                </CardTitle>
                <div className="flex flex-wrap gap-1 pt-1">
                  {s.course && <Badge variant="outline" className="text-[10px]">{s.course}</Badge>}
                  {(Array.isArray(s.batches) && s.batches.length > 0 ? s.batches : (s.batch ? [s.batch] : [])).map(b => <Badge key={b} variant="secondary" className="text-[10px]">{b}</Badge>)}
                </div>
              </CardHeader>
              <CardContent className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><CalendarIcon className="h-3.5 w-3.5" /> {s.date} · {s.start_time} ({s.duration_minutes} min)</div>
                {((Array.isArray(s.locations) && s.locations.length > 0) || s.location) && (
                  <div className="flex items-start gap-2 text-xs text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span className="break-words">{(Array.isArray(s.locations) && s.locations.length > 0 ? s.locations : [s.location]).filter(Boolean).join(' · ')}</span>
                  </div>
                )}
                {s.notes && <p className="text-xs">{s.notes}</p>}
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Phase 254-Bulk-Pay — Confirmation Dialog with itemized per-child breakdown */}
      <Dialog open={bulkDialogOpen} onOpenChange={(o) => { if (!bulkPaying) setBulkDialogOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="parent-bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-emerald-600" />
              Confirm Household Bulk Payment
            </DialogTitle>
            <DialogDescription>
              Review the fees being settled below. One Razorpay transaction will clear every line for {bulkCart?.academy_name || 'your academy'}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto py-2 -mx-1 px-1">
            {(bulkCart?.by_child || []).map((c) => (
              <div key={c.student_id} className="rounded-lg border border-border/60 p-3 space-y-2" data-testid={`bulk-child-block-${c.student_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold">{c.student_name}</p>
                  <p className="text-sm font-mono font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
                    ₹{(c.subtotal || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="space-y-1">
                  {(c.fees || []).map((f) => (
                    <div key={f.id} className="flex items-center justify-between text-xs text-muted-foreground" data-testid={`bulk-fee-line-${f.id}`}>
                      <span className="truncate pr-2">
                        {f.fee_type || 'Fee'}
                        {f.due_date && <span className="text-[10px] ml-1">· due {f.due_date}</span>}
                      </span>
                      <span className="tabular-nums font-medium text-foreground/70 flex-shrink-0">
                        ₹{(f.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 px-4 py-3 flex items-center justify-between" data-testid="bulk-grand-total">
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">Grand Total</span>
            <span className="text-2xl font-display font-bold tabular-nums text-emerald-800 dark:text-emerald-100">
              ₹{(bulkCart?.total_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </span>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setBulkDialogOpen(false)}
              disabled={bulkPaying}
              data-testid="parent-bulk-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkPay}
              disabled={bulkPaying || !bulkCart?.eligible}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="parent-bulk-confirm-pay-btn"
            >
              {bulkPaying ? (
                <><Clock className="h-4 w-4 mr-2 animate-spin" /> Opening Razorpay…</>
              ) : (
                <><CreditCard className="h-4 w-4 mr-2" /> Confirm &amp; Pay ₹{(bulkCart?.total_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Phase 104 — Targeted Practice Kit (AI-prescribed remedial drills for low metrics)
function TargetedPracticeKit({ cardId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!cardId) return;
    let active = true;
    setLoading(true);
    api.get(`/progress-cards/${cardId}/drill-recommendations`)
      .then((r) => { if (active) setData(r.data); })
      .catch(() => { if (active) setData({ recommendations: [] }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [cardId]);

  if (loading) return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
      AI is identifying weaknesses and prescribing remedial drills…
    </div>
  );
  if (!data || !data.recommendations || data.recommendations.length === 0) return null;

  return (
    <div className="rounded-lg border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/20 dark:to-pink-950/20 p-4 space-y-3" data-testid={`practice-kit-${cardId}`}>
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-rose-600" />
        <p className="font-semibold text-sm text-rose-900">🎯 AI Coach Recommendation: Targeted Practice Kit</p>
      </div>
      {data.recommendations.map((rec) => (
        <div key={rec.skill} className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs">
            <Badge variant="outline" className="bg-rose-100 text-rose-800 border-rose-300 text-[10px]" data-testid={`kit-skill-${rec.skill}`}>
              Weakness: {rec.skill} · {rec.score}/{rec.scale}
            </Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {rec.drills.map((d) => (
              <div key={d.id} className="rounded-md border border-rose-200 bg-background p-2.5 space-y-1.5" data-testid={`kit-drill-${d.id}`}>
                <p className="font-medium text-xs leading-tight line-clamp-2">{d.title}</p>
                <div className="flex flex-wrap gap-1">
                  {d.duration_minutes && <Badge variant="outline" className="text-[9px] gap-0.5"><Clock className="h-2.5 w-2.5" />{d.duration_minutes}m</Badge>}
                  {d.difficulty && <Badge variant="outline" className="text-[9px] capitalize">{d.difficulty}</Badge>}
                </div>
                {d.justification && <p className="text-[10px] text-muted-foreground italic line-clamp-2">"{d.justification}"</p>}
                {d.video_url && (
                  <a href={d.video_url} target="_blank" rel="noreferrer" className="text-rose-600 hover:underline text-[11px] inline-flex items-center gap-0.5" data-testid={`kit-drill-video-${d.id}`}>
                    ▶ Watch
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className="text-[10px] text-muted-foreground pt-1">Drills are AI-matched to weaknesses from your academy's drill library. Coach can also assign these in your next session.</p>
    </div>
  );
}